/**
 * Keeps unsaved Manage product fields across scroll, photo upload, and links
 * that come back to this product (`return_to` or `data-preserve-form`).
 * phx-change only sends a text field after it blurs. Closing the editor is a
 * normal link: the LiveView exits and drops the draft.
 *
 *      <div id="product-edit-page" phx-hook="PreserveProductForm">
 */
const PreserveProductForm = {
  mounted() {
    this.lastKey = null;
    this.onScroll = () => this.flush();
    this.onPointerDown = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest("a[href]")) return;
      const form = productForm();
      if (!form || form.contains(event.target)) return;
      this.flush();
    };
    this.onClick = (event) => this.maybeNavigate(event);

    window.addEventListener("scroll", this.onScroll, true);
    document.addEventListener("pointerdown", this.onPointerDown, true);
    document.addEventListener("click", this.onClick, true);
  },

  destroyed() {
    window.removeEventListener("scroll", this.onScroll, true);
    document.removeEventListener("pointerdown", this.onPointerDown, true);
    document.removeEventListener("click", this.onClick, true);
  },

  flush() {
    if (!this.liveSocket.isConnected()) return;
    const form = productForm();
    if (!form || !formDirty(form)) return;
    this.pushForm(form, null);
  },

  maybeNavigate(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest("a[href]");
    if (!link) return;
    if (link.target && link.target !== "_self") return;
    if (!returnsToProduct(link)) return;

    const href = link.getAttribute("href");
    if (!href || href.startsWith("#") || href.includes(":")) return;

    const form = productForm();
    if (!form) return;
    // LiveView follows href itself when the socket is down. Cancelling the
    // click first makes the link do nothing until the socket returns.
    if (!this.liveSocket.isConnected()) return;

    event.preventDefault();
    event.stopPropagation();
    this.pushForm(form, href);
  },

  pushForm(form, href) {
    const product = productParams(form);
    const key = JSON.stringify(product);
    if (!href && key === this.lastKey) return;
    this.lastKey = key;

    const payload = { product };
    if (href) payload.href = href;
    // LiveView 1.2 returns a promise only from the two-argument form.
    // The onReply callback returns undefined, so catch would never attach.
    this.pushEvent("preserve-product-form", payload).catch(() => {
      if (href) window.location.assign(href);
    });
  },
};

function returnsToProduct(link) {
  if (link.hasAttribute("data-preserve-form")) return true;

  const href = link.getAttribute("href");
  if (!href) return false;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch (_e) {
    return false;
  }

  const returnTo = url.searchParams.get("return_to");
  if (!returnTo) return false;
  return returnTo.split("#")[0] === window.location.pathname;
}

function productForm() {
  return document.getElementById("product-form");
}

function productParams(form) {
  const product = {};
  for (const [key, value] of new FormData(form).entries()) {
    const match = key.match(/^product\[([^\]]+)\]$/);
    if (match) product[match[1]] = value;
  }
  return product;
}

function formDirty(form) {
  return Array.from(form.elements).some(controlDirty);
}

function controlDirty(el) {
  if (!el.name || el.disabled || el.type === "hidden" || el.type === "submit") {
    return false;
  }
  if (el.type === "checkbox" || el.type === "radio") {
    return el.checked !== el.defaultChecked;
  }
  if (el.tagName === "SELECT") {
    return Array.from(el.options).some(
      (option) => option.selected !== option.defaultSelected,
    );
  }
  return typeof el.defaultValue === "string" && el.value !== el.defaultValue;
}

export default PreserveProductForm;
